import { isSafeUrl } from '../url-safety';

export interface LocationCardData {
  name?: string;
  address?: string;
  city_state_zip?: string;
  phone?: string;
  map_url?: string;
}

interface LocationCardProps {
  data: LocationCardData;
}

function buildDirectionsUrl(data: LocationCardData): string {
  if (isSafeUrl(data.map_url)) return data.map_url;
  const query = encodeURIComponent(
    [data.name, data.address, data.city_state_zip].filter(Boolean).join(', '),
  );
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
}

const PinIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const PhoneIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

export function LocationCard({ data }: LocationCardProps): React.JSX.Element | null {
  const { name, address, city_state_zip, phone } = data;

  if (!name && !address) return null;

  const directionsHref = buildDirectionsUrl(data);
  const telHref = phone ? `tel:${phone.replace(/[^0-9+]/g, '')}` : null;

  return (
    <div className="loc-card">
      <div className="loc-card-body">
        {name && <div className="loc-card-name">{name}</div>}
        {address && (
          <div className="loc-card-address">
            <span className="loc-card-pin" aria-hidden="true">{PinIcon}</span>
            <span>
              {address}
              {city_state_zip ? (
                <>
                  <br />
                  {city_state_zip}
                </>
              ) : null}
            </span>
          </div>
        )}
        <div className="loc-card-actions">
          <a
            className="loc-card-cta"
            href={directionsHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="loc-card-cta-icon" aria-hidden="true">{PinIcon}</span>
            Get Directions
          </a>
          {telHref && (
            <a className="loc-card-cta loc-card-cta-secondary" href={telHref}>
              <span className="loc-card-cta-icon" aria-hidden="true">{PhoneIcon}</span>
              Call
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
